import React, { useState, useEffect, useRef } from "react";
import EmojiPicker from "emoji-picker-react";
import { Helmet } from "react-helmet";
import Sidebar from "../../Layouts/Sidebar";
import ChatService from "../../../Services/ChatService";
import SocketService from "../../../Services/SocketService";
import DataParser from "../../../utlits/Dataparser";
import RoomList from "./RoomList";
import MessageInput from "./MessageInput";
import { useChatContext } from "./ChatContext";
import { Modal, Avatar, Tooltip, Empty, Tabs, Input, Button, Dropdown, Menu, Popconfirm, message, Popover, Drawer } from "antd";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { UserPlus, Info, MoreVertical, Paperclip, Smile, Trash2, LogOut, Eraser, Reply, SmilePlus, Search, X, Briefcase, Hash, Users, MessageSquare, Pin, PinOff, Check, CheckCheck, FileText } from "lucide-react";
import moment from "moment";
import "./Chat.css";

const getImageUrl = (url) => {
    if (!url || url === "null") return null;
    if (url.startsWith('http')) return url;
    const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split("/api/v1")[0];
    
    if (url.includes('/')) {
        const normalizedUrl = url.startsWith("/") ? url : `/${url}`;
        return `${baseUrl}${normalizedUrl.startsWith("/public") ? normalizedUrl : `/public${normalizedUrl}`}`;
    }
    
    return (process.env.REACT_APP_USER_DOCS_PATH || `${baseUrl}/public/users/docs/`) + url;
};

const { TabPane } = Tabs;

const _shimmer = { background: "linear-gradient(90deg,#f0f0f0 25%,#e8e8e8 50%,#f0f0f0 75%)", backgroundSize: "600px 100%", animation: "chat-shimmer 1.4s infinite linear", borderRadius: 6 };
const ChatShimmer = () => {
    const rows = [{ own: false, lines: [200, 140] }, { own: true, lines: [160] }, { own: false, lines: [240, 120, 80] }, { own: true, lines: [180, 100] }, { own: false, lines: [130] }, { own: true, lines: [220, 80] }, { own: false, lines: [170, 110] }];
    return (
        <div style={{ display: "flex", flexDirection: "column", padding: "16px 24px", gap: 16, flex: 1 }}>
            {rows.map((row, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-end", gap: 10, flexDirection: row.own ? "row-reverse" : "row" }}>
                    <div style={{ ..._shimmer, width: 28, height: 28, borderRadius: "50%", flexShrink: 0 }} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxWidth: "60%" }}>
                        {row.lines.map((w, j) => <div key={j} style={{ ..._shimmer, height: 14, width: w }} />)}
                        <div style={{ ..._shimmer, height: 8, width: 40, alignSelf: row.own ? "flex-end" : "flex-start" }} />
                    </div>
                </div>
            ))}
        </div>
    );
};

const Chat = () => {
    const { openChat } = useChatContext();
    const [rooms, setRooms] = useState([]);
    const [activeRoom, setActiveRoom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msgLoading, setMsgLoading] = useState(false);
    const [pinnedMessages, setPinnedMessages] = useState([]);
    const [showPinnedDrawer, setShowPinnedDrawer] = useState(false);
    const [typingUsers, setTypingUsers] = useState({}); // { roomId: { userId: userName } }
    const [isNewChatModal, setIsNewChatModal] = useState(false);
    const [isNewDmMode, setIsNewDmMode] = useState(false);
    const [isAddMemberModal, setIsAddMemberModal] = useState(false);
    const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
    const [replyTo, setReplyTo] = useState(null);
    const [openReactionPickerId, setOpenReactionPickerId] = useState(null);
    const [reactionPickerDir, setReactionPickerDir] = useState({ v: 'up', h: 'right' });

    useEffect(() => {
        if (!openReactionPickerId) return;
        const close = (e) => { if (!e.target.closest('.reaction-picker-wrapper')) setOpenReactionPickerId(null); };
        const t = setTimeout(() => document.addEventListener('mousedown', close), 0);
        return () => { clearTimeout(t); document.removeEventListener('mousedown', close); };
    }, [openReactionPickerId]);
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [chatSearchQuery, setChatSearchQuery] = useState("");
    const [parentRoomId, setParentRoomId] = useState(null);
    const [modalType, setModalType] = useState('dm'); // 'dm' or 'channel'
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [newChannelName, setNewChannelName] = useState("");
    const [mentionUsers, setMentionUsers] = useState([]);
    const [urlPreviews, setUrlPreviews] = useState({}); // { url: previewData }

    const renderMessageText = (text) => {
        if (!text) return "";
        const urlRegex = /(https?:\/\/\S+|www\.\S+)/g;
        // Split by mentions first, then linkify URLs inside plain-text segments
        const parts = text.split(/(@\[[^\]]+\]\(\d+\))/g);
        const result = [];
        parts.forEach((part, i) => {
            const mentionMatch = part.match(/@\[([^\]]+)\]\((\d+)\)/);
            if (mentionMatch) {
                result.push(<span key={`m-${i}`} style={{ color: '#1a73e8', fontWeight: '500', cursor: 'pointer' }}>@{mentionMatch[1]}</span>);
                return;
            }
            // Split plain text by URL pattern (capture group keeps the URL in the array)
            const urlParts = part.split(urlRegex);
            urlParts.forEach((up, j) => {
                if (!up) return;
                if (/^(https?:\/\/|www\.)[^\s]+/.test(up)) {
                    const href = up.startsWith('http') ? up : `https://${up}`;
                    result.push(
                        <a key={`u-${i}-${j}`} href={href} target="_blank" rel="noopener noreferrer"
                           style={{ color: '#1a73e8', textDecoration: 'underline', wordBreak: 'break-all' }}>
                            {up}
                        </a>
                    );
                } else {
                    result.push(up);
                }
            });
        });
        return result;
    };

    const currentUser = DataParser.GetLoggedInUser();
    const currentUserId = currentUser?.user?.userdata?.id;
    const scrollRef = useRef(null);
    const activeRoomRef = useRef(null);
    useEffect(() => { activeRoomRef.current = activeRoom; }, [activeRoom]);

    const isDeletedMember = (m) => !m || m.is_deleted === true || m.status === 0 || m.status === "0";

    const getRoomDisplayName = (room) => {
        if (!room) return "";
        if (room.type === "direct" && room.members?.length > 0) {
            const otherMember = room.members.find(m => String(m.id || m._id) !== String(currentUserId));
            if (isDeletedMember(otherMember)) return "Deleted User";
            const name = `${otherMember.first_name || ""} ${otherMember.last_name || ""}`.trim();
            return name || "Deleted User";
        }
        return room.name || "Unnamed Room";
    };

    const getRoomDisplayAvatar = (room) => {
        if (room?.type === "direct") {
            const otherMember = room.members.find(m => String(m.id || m._id) !== String(currentUserId));
            if (isDeletedMember(otherMember)) return null;
            return otherMember?.image ? getImageUrl(otherMember.image) : null;
        }
        return null;
    };

    // 🟢 Initialization
    useEffect(() => {
        if (activeRoom) {
            setMessages([]);
            setMsgLoading(true);
            fetchMessages();
            fetchPinnedMessages();
        }
    }, [activeRoom?._id]);

    const fetchPinnedMessages = async () => {
        try {
            const res = await ChatService.getPinnedMessages(activeRoom._id);
            if (res.Good) setPinnedMessages(res.pinned);
        } catch (err) { }
    };

    const handleTogglePin = async (messageId) => {
        try {
            const res = await ChatService.togglePin({ messageId });
            if (res.Good) {
                // Broadcast to others
                SocketService.emit("messagePinned", {
                    room_id: activeRoom._id,
                    messageId,
                    isPinned: res.is_pinned,
                    message: res.message
                });

                // Update local state is handled by the common update logic
                setMessages(prev => prev.map(m => m._id === messageId ? { ...m, is_pinned: res.is_pinned } : m));
                if (res.is_pinned) {
                    setPinnedMessages(prev => {
                        if (prev.find(p => p._id === messageId)) return prev;
                        return [...prev, res.message];
                    });
                    message.success("Message pinned");
                } else {
                    setPinnedMessages(prev => prev.filter(m => m._id !== messageId));
                    message.success("Message unpinned");
                }
            }
        } catch (err) {
            message.error("Failed to pin message");
        }
    };

    useEffect(() => {
        // Run once on mount — not on every room click
        fetchRooms();
        fetchMentionUsers();

        const handleNewRoom = (data) => {
            setRooms(prev => {
                const dataId = String(data._id || data.id || "");
                if (!dataId || prev.find(r => String(r._id || r.id || "") === dataId)) return prev;
                SocketService.emit("joinChatRoom", data._id || data.id);
                return [data, ...prev];
            });
        };

        const handleNewMessage = (data) => {
            const dataRoomId = String(data.room_id);
            const isActive = String(activeRoomRef.current?._id) === dataRoomId;
            // Add to message list only if the user is currently viewing this room
            if (isActive && String(data.sender_id) !== String(currentUserId)) {
                setMessages(prev => {
                    const dataId = data._id || data.id;
                    if (prev.find(m => String(m._id || m.id) === String(dataId))) return prev;
                    return [...prev, data];
                });
                scrollToBottom();
                // Mark as read immediately — receiver is actively watching this room
                ChatService.markAsRead(activeRoomRef.current._id).catch(() => {});
                SocketService.emit("messagesRead", { room_id: activeRoomRef.current._id, user_id: String(currentUserId) });
            }
            // Sound / OS notification / delivery receipt are handled by ChatContext (always mounted).
            setRooms(prev => prev.map(r => {
                if (String(r._id) !== dataRoomId) return r;
                return {
                    ...r,
                    unreadCount: isActive ? 0 : (r.unreadCount || 0) + 1,
                    last_message: data
                };
            }));
        };

        const handleTyping = (data) => {
            const { room_id, user_id, user_name, isTyping } = data;
            setTypingUsers(prev => {
                const roomTyping = { ...(prev[room_id] || {}) };
                if (isTyping) roomTyping[user_id] = user_name;
                else delete roomTyping[user_id];
                return { ...prev, [room_id]: roomTyping };
            });
        };

        const handleReactionFromSocket = (data) => {
            const { messageId, reactions } = data;
            setMessages(prev => prev.map(m =>
                m._id === messageId ? { ...m, reactions } : m
            ));
        };

        const handleMessagesRead = (data) => {
            if (activeRoomRef.current && String(activeRoomRef.current._id) === String(data.room_id)) {
                setMessages(prev => prev.map(m =>
                    String(m.sender_id) === String(currentUserId)
                        ? { ...m, is_read: [data.user_id] }
                        : m
                ));
            }
        };

        const handleMessagePinned = (data) => {
            const { messageId, isPinned, message } = data;
            setMessages(prev => prev.map(m => m._id === messageId ? { ...m, is_pinned: isPinned } : m));
            if (isPinned) {
                setPinnedMessages(prev => prev.find(p => p._id === messageId) ? prev : [...prev, message]);
            } else {
                setPinnedMessages(prev => prev.filter(m => m._id !== messageId));
            }
        };

        const handleUserOnline = (onlineUserId) => {
            setRooms(prev => prev.map(room => ({
                ...room,
                members: (room.members || []).map(m => String(m.id || m._id) === String(onlineUserId) ? { ...m, isOnline: true } : m)
            })));
            setActiveRoom(prev => prev ? { ...prev, members: (prev.members || []).map(m => String(m.id || m._id) === String(onlineUserId) ? { ...m, isOnline: true } : m) } : prev);
        };

        const handleUserOffline = (offlineUserId) => {
            setRooms(prev => prev.map(room => ({
                ...room,
                members: (room.members || []).map(m => String(m.id || m._id) === String(offlineUserId) ? { ...m, isOnline: false } : m)
            })));
            setActiveRoom(prev => prev ? { ...prev, members: (prev.members || []).map(m => String(m.id || m._id) === String(offlineUserId) ? { ...m, isOnline: false } : m) } : prev);
        };

        const handleChatCleared = (data) => {
            if (String(activeRoomRef.current?._id) === String(data.roomId)) setMessages([]);
        };

        const handleUserLeftRoom = (data) => {
            const roomId = String(data.roomId);
            const leftId = String(data.userId);
            setRooms(prev => prev.map(r =>
                String(r._id) === roomId
                    ? { ...r, members: (r.members || []).filter(m => String(m.id || m._id) !== leftId) }
                    : r
            ));
            if (String(activeRoomRef.current?._id) === roomId) {
                setActiveRoom(prev => prev
                    ? { ...prev, members: (prev.members || []).filter(m => String(m.id || m._id) !== leftId) }
                    : prev
                );
            }
        };

        const handleRoomDeleted = (data) => {
            const roomId = String(data.roomId);
            setRooms(prev => prev.filter(r => String(r._id) !== roomId));
            if (String(activeRoomRef.current?._id) === roomId) setActiveRoom(null);
        };

        const handleMemberJoined = ({ room }) => {
            setRooms(prev => prev.map(r => r._id === room._id ? { ...r, members: room.members } : r));
            if (String(activeRoomRef.current?._id) === room._id)
                setActiveRoom(prev => prev ? { ...prev, members: room.members } : prev);
        };

        SocketService.on("receiveChatMessage", handleNewMessage);
        SocketService.on("userTyping", handleTyping);
        SocketService.on("messageReaction", handleReactionFromSocket);
        SocketService.on("newRoom", handleNewRoom);
        SocketService.on("messagePinned", handleMessagePinned);
        SocketService.on("userOnline", handleUserOnline);
        SocketService.on("userOffline", handleUserOffline);
        SocketService.on("messagesRead", handleMessagesRead);
        SocketService.on("chatCleared", handleChatCleared);
        SocketService.on("userLeftRoom", handleUserLeftRoom);
        SocketService.on("roomDeleted", handleRoomDeleted);
        SocketService.on("memberJoined", handleMemberJoined);

        return () => {
            SocketService.off("receiveChatMessage", handleNewMessage);
            SocketService.off("userTyping", handleTyping);
            SocketService.off("messageReaction", handleReactionFromSocket);
            SocketService.off("newRoom", handleNewRoom);
            SocketService.off("messagePinned", handleMessagePinned);
            SocketService.off("userOnline", handleUserOnline);
            SocketService.off("userOffline", handleUserOffline);
            SocketService.off("messagesRead", handleMessagesRead);
            SocketService.off("chatCleared", handleChatCleared);
            SocketService.off("userLeftRoom", handleUserLeftRoom);
            SocketService.off("roomDeleted", handleRoomDeleted);
            SocketService.off("memberJoined", handleMemberJoined);
        };
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const fetchRooms = async () => {
        try {
            const res = await ChatService.getRooms();
            if (res.Good) {
                setRooms(res.rooms);
                setLoading(false);
            }
        } catch (err) {
            console.error("Fetch rooms error", err);
            setLoading(false);
        }
    };

    const fetchMessages = async () => {
        try {
            const res = await ChatService.getMessages(activeRoom._id, 1, activeRoom.my_visible_from || null);
            if (res.Good) {
                setMessages(res.messages);
            }
        } catch (err) {
            console.error("Fetch messages error", err);
        } finally {
            setMsgLoading(false);
        }
    };

    const fetchMentionUsers = async () => {
        try {
            if (!currentUser?.user?.userdata?.company_id) return;
            const resp = await ChatService.getMentionUsers(currentUser.user.userdata.company_id);
            if (resp.Good) {
                setMentionUsers(resp.data.user.map(u => ({ id: u.id, display: u.full_name })));
            }
        } catch (err) { }
    };

    const handleSelectRoom = async (room, rect) => {
        openChat(room, rect);
        setActiveRoom(room);
        setReplyTo(null);
        setMsgLoading(true);
        SocketService.emit("joinChatRoom", room._id);
        ChatService.markAsRead(room._id).catch(() => {});
        SocketService.emit("messagesRead", { room_id: room._id, user_id: String(currentUserId) });

        try {
            const res = await ChatService.getMessages(room._id, 1, room.my_visible_from || null);
            if (res.Good) {
                setMessages(res.messages);
            }
        } catch (err) {
            console.error("Fetch messages error", err);
        } finally {
            setMsgLoading(false);
        }
    };

    const formatDateDivider = (dateString) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(today.getDate() - 1);

        if (date.toDateString() === today.toDateString()) return "Today";
        if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

        return date.toLocaleDateString([], {
            weekday: 'long',
            month: 'short',
            day: 'numeric'
        });
    };

    const handleSendMessage = async (payload) => {
        if (!activeRoom) return;
        const { text, attachments, topic_id, topic_name, parent_id } = payload;

        const tempId = `temp-${Date.now()}`;
        const tempMsg = {
            _id: tempId,
            id: tempId,
            text,
            sender_id: currentUserId,
            sender_name: (currentUser?.user?.userdata?.first_name || "") + " " + (currentUser?.user?.userdata?.last_name || ""),
            sender_image: currentUser?.user?.userdata?.image,
            createdAt: new Date().toISOString(),
            isSending: true,
            attachments: (attachments || []).map(f => ({
                name: f.name,
                fileType: f.type?.split('/')[0] || (f.name?.match(/\.(mp3|wav|webm|m4a|ogg)$/i) ? 'audio' : 'file'),
                url: URL.createObjectURL(f),
                isLocal: true
            })),
            parent_id: replyTo
        };

        setMessages(prev => [...prev, tempMsg]);

        try {
            let messageData;
            const body = { room_id: activeRoom._id, text, topic_id, topic_name, parent_id };

            if (attachments && attachments.length > 0) {
                const formData = new FormData();
                Object.keys(body).forEach(key => {
                    if (body[key]) formData.append(key, body[key]);
                });
                attachments.forEach(file => formData.append("attachments", file));

                const res = await ChatService.sendMessage(formData);
                messageData = res.message;
            } else {
                const res = await ChatService.sendMessage(body);
                messageData = res.message;
            }

            if (messageData) {
                const fullMsg = {
                    ...messageData,
                    sender_name: (currentUser?.user?.userdata?.first_name || "") + " " + (currentUser?.user?.userdata?.last_name || ""),
                    sender_image: currentUser?.user?.userdata?.image,
                    parent_id: replyTo
                };

                // SocketService.emit("sendChatMessage", fullMsg); // Removed redundant emit as backend should handle broadcasting
                setMessages(prev => prev.map(m => (m._id === tempId || m.id === tempId) ? { ...fullMsg, isSending: false } : m));
                setReplyTo(null);
                detectAndFetchPreviews(text);
            }
        } catch (err) {
            console.error("Send message error", err);
            setMessages(prev => prev.filter(m => m._id !== tempId && m.id !== tempId));
        }
    };

    const detectAndFetchPreviews = (text) => {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const urls = text.match(urlRegex);
        if (urls) {
            urls.forEach(async (url) => {
                if (!urlPreviews[url]) {
                    try {
                        const res = await ChatService.getUrlPreview(url);
                        if (res.Good) {
                            setUrlPreviews(prev => ({ ...prev, [url]: res.data }));
                        }
                    } catch (err) { }
                }
            });
        }
    };

    const handleTyping = (isTyping) => {
        if (!activeRoom) return;
        SocketService.emit("typing", {
            room_id: activeRoom._id,
            user_id: currentUserId,
            user_name: currentUser?.user?.userdata?.first_name || "Someone",
            isTyping
        });
    };

    const handleSearchUsers = async (val) => {
        setSearchQuery(val);
        if (val.length < 2) return setSearchResults([]);
        try {
            const res = await ChatService.searchUsers(val);
            if (res.Good) setSearchResults(res.users);
        } catch (err) { }
    };

    const handleStartDirectChat = async (userId) => {
        try {
            const res = await ChatService.getOrCreateRoom({ type: "direct", targetId: userId });
            if (res.Good) {
                const newRoom = res.room;
                const newId = String(newRoom._id || newRoom.id || "");
                const existing = rooms.find(r => String(r._id || r.id || "") === newId);
                if (!existing) {
                    setRooms([newRoom, ...rooms]);
                }
                handleSelectRoom(existing || newRoom);
                setIsNewChatModal(false);
                setIsNewDmMode(false);
            }
        } catch (err) { }
    };

    const handleNewChat = (type = 'dm', parentId = null) => {
        if (type === 'dm') {
            setActiveRoom(null);
            setMessages([]);
            setIsNewDmMode(true);
            setIsNewChatModal(false);
        } else {
            setModalType(type);
            setParentRoomId(parentId);
            setSearchQuery("");
            setSearchResults([]);
            setNewChannelName("");
            setIsNewChatModal(true);
            setIsNewDmMode(false);
        }
    };

    const handleCreateChannel = async () => {
        if (!newChannelName.trim()) return;
        try {
            const payload = { type: "channel", name: newChannelName };
            if (parentRoomId) payload.parent_id = parentRoomId;
            const res = await ChatService.getOrCreateRoom(payload);
            if (res.Good) {
                const newRoom = res.room;
                const newId = String(newRoom._id || newRoom.id || "");
                const existing = rooms.find(r => String(r._id || r.id || "") === newId);
                if (!existing) {
                    setRooms([newRoom, ...rooms]);
                }
                handleSelectRoom(existing || newRoom);
                setIsNewChatModal(false);
                setNewChannelName("");
                setParentRoomId(null);
            }
        } catch (err) { }
    };

    const handleConfirmAddMember = async (userId) => {
        if (!activeRoom) return;
        try {
            const res = await ChatService.addMember({ roomId: activeRoom._id, userId });
            if (res.Good) {
                const updatedRoom = {
                    ...activeRoom,
                    members: [...activeRoom.members, res.user]
                };
                setActiveRoom(updatedRoom);
                setRooms(rooms.map(r => r._id === activeRoom._id ? updatedRoom : r));
                SocketService.emit("memberJoined", { room: updatedRoom });
                setIsAddMemberModal(false);
                setSearchQuery("");
                setSearchResults([]);
                message.success("Member added!");
            }
        } catch (err) { }
    };

    const handleClearChat = async () => {
        try {
            const res = await ChatService.clearMessages({ roomId: activeRoom._id });
            if (res.Good) {
                setMessages([]);
                SocketService.emit("chatCleared", { roomId: activeRoom._id });
                message.success("Chat history cleared");
            }
        } catch (err) {
            message.error("Failed to clear chat");
        }
    };

    const handleLeaveRoom = async () => {
        try {
            const res = await ChatService.leaveRoom({ roomId: activeRoom._id });
            if (res.Good) {
                SocketService.emit("userLeftRoom", { roomId: activeRoom._id, userId: String(currentUserId) });
                setRooms(rooms.filter(r => r._id !== activeRoom._id));
                setActiveRoom(null);
                message.success("Left room successfully");
            }
        } catch (err) {
            message.error("Failed to leave room");
        }
    };

    const handleDeleteRoom = async () => {
        try {
            const res = await ChatService.deleteRoom({ roomId: activeRoom._id });
            if (res.Good) {
                SocketService.emit("roomDeleted", { roomId: activeRoom._id });
                setRooms(rooms.filter(r => r._id !== activeRoom._id));
                setActiveRoom(null);
                message.success("Room deleted successfully");
            }
        } catch (err) {
            message.error("Failed to delete room");
        }
    };

    const handleToggleReaction = async (messageId, emoji) => {
        try {
            const res = await ChatService.toggleReaction({ messageId, emoji });
            if (res.Good) {
                // Inform others via socket
                SocketService.emit("messageReaction", {
                    room_id: activeRoom._id,
                    messageId,
                    reactions: res.reactions
                });
                // Update local state
                setMessages(prev => prev.map(m =>
                    m._id === messageId ? { ...m, reactions: res.reactions } : m
                ));
            }
        } catch (err) { }
    };

    const scrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    const scrollToMessage = (msgId) => {
        const element = document.getElementById(`msg-${msgId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            element.classList.add('highlight-message');
            setTimeout(() => element.classList.remove('highlight-message'), 2000);
        }
        setShowPinnedDrawer(false);
    };

    const renderMessage = (msg, idx, showTime = true) => {
        const isOwn = String(msg.sender_id) === String(currentUserId);
        const isGrouped = !showTime; // same sender, same minute as previous message
        const senderMember = activeRoom?.members?.find(m => String(m.id || m._id) === String(msg.sender_id));
        const isSenderDeleted = isDeletedMember(senderMember) && senderMember !== undefined;
        const displaySenderName = isSenderDeleted ? "Deleted User" : (msg.sender_name || "Deleted User");
        const displaySenderImage = isSenderDeleted ? null : msg.sender_image;
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const urls = msg.text?.match(urlRegex);

        const reactionsList = msg.reactions || [];

        return (
            <div
                key={msg._id || idx}
                id={`msg-${msg._id}`}
                className={`message-item ${isOwn ? "own" : ""}`}
                style={isGrouped ? { marginTop: '-10px' } : undefined}
            >
                {!isGrouped && (
                    <Avatar
                        size={36}
                        src={displaySenderImage ? getImageUrl(displaySenderImage) : null}
                        style={{ background: isSenderDeleted ? "#9ca3af" : "#0DDFAB", color: "#fff", fontSize: 13, flexShrink: 0, marginTop: '4px' }}
                    >
                        {displaySenderName?.charAt(0)}
                    </Avatar>
                )}
                <div className="message-wrapper" style={{ display: 'flex', flexDirection: 'column', alignItems: isOwn ? 'flex-end' : 'flex-start', minWidth: 0 }}>
                    {!isGrouped && activeRoom?.type !== "direct" && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'baseline',
                            gap: '8px',
                            marginBottom: '2px',
                            padding: '0 4px'
                        }}>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: '#202124' }}>{isOwn ? "You" : displaySenderName} | {moment(msg.createdAt).format("h:mm A")}</div>
                        </div>
                    )}

                    <div className="message-content" style={{ maxWidth: '100%', opacity: msg.isSending ? 0.6 : 1 }}>
                        {/* Pin status indicator */}
                        {msg.is_pinned && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#1a73e8', marginBottom: '4px' }}>
                                <Pin size={10} fill="#1a73e8" />
                                <span>Pinned</span>
                            </div>
                        )}

                        {/* Quoted Parent Message */}
                        {msg.parent_id && (
                            <div className="quoted-message" style={{ background: 'rgba(0,0,0,0.05)', borderLeft: '3px solid #1a73e8', padding: '5px 10px', borderRadius: '4px', marginBottom: '8px', fontSize: '11px', opacity: 0.8 }}>
                                <div style={{ fontWeight: 'bold', color: '#1a73e8' }}>{msg.parent_id.sender_name || "Deleted User"}</div>
                                <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {msg.parent_id.text || "Attachment"}
                                </div>
                            </div>
                        )}

                        <div className="message-text-wrapper" style={{ position: 'relative' }}>
                            <div>{renderMessageText(msg.text)}</div>



                            {/* Hover Actions */}
                            <div className={`message-actions-toolbar${openReactionPickerId === msg._id ? ' reaction-open' : ''}`}>
                                <Tooltip title={msg.is_pinned ? "Unpin" : "Pin"}>
                                    <Pin size={16}
                                        onClick={() => handleTogglePin(msg._id)}
                                        style={{ cursor: 'pointer', color: msg.is_pinned ? '#1a73e8' : '#5f6368' }}
                                    />
                                </Tooltip>
                                <Tooltip title="Reply">
                                    <Reply size={16} onClick={() => setReplyTo(msg)} style={{ cursor: 'pointer' }} />
                                </Tooltip>
                                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                    <SmilePlus size={16} style={{ cursor: 'pointer' }} onClick={(e) => { const rect = e.currentTarget.getBoundingClientRect(); setReactionPickerDir({ v: window.innerHeight - rect.bottom < 390 ? 'up' : 'down', h: rect.left + rect.width / 2 < window.innerWidth / 2 ? 'left' : 'right' }); setOpenReactionPickerId(openReactionPickerId === msg._id ? null : msg._id); }} />
                                    {openReactionPickerId === msg._id && (
                                      <div className="reaction-picker-wrapper" style={{ position: 'absolute', ...(reactionPickerDir.v === 'up' ? { bottom: '30px' } : { top: '30px' }), ...(reactionPickerDir.h === 'right' ? { right: 0 } : { left: 0 }), zIndex: 100, background: '#fff', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: '12px' }} onClick={e => e.stopPropagation()}>
                                        <div className="float-input-wrap float-input-icon" style={{ marginBottom: '4px' }}>
                                          <i className="uil-search" style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '15px', pointerEvents: 'none', zIndex: 2 }} />
                                          <input type="search" autoComplete="off" data-form-type="other" placeholder=" " className="form-control-custom-focus" style={{ padding: '0 38px 0 14px', borderRadius: '8px', height: '38px', border: '1.5px solid #d0d0d0', lineHeight: '38px', width: '100%' }} onChange={e => { const val = e.target.value; const root = e.currentTarget.closest('.reaction-picker-wrapper'); const inp = root?.querySelector('input:not(.form-control-custom-focus)'); if (inp) { const s = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set; s.call(inp, val); inp.dispatchEvent(new Event('input', { bubbles: true })); inp.dispatchEvent(new Event('change', { bubbles: true })); } }} />
                                          <label style={{ fontSize: '13px', left: '14px' }}>Search emojis...</label>
                                        </div>
                                        <EmojiPicker
                                            onEmojiClick={(emojiData) => { handleToggleReaction(msg._id, emojiData.emoji); setOpenReactionPickerId(null); }}
                                            skinTonesDisabled
                                            previewConfig={{ showPreview: false }}
                                            width={350}
                                            height={300}
                                        />
                                      </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                            <div className="message-attachments" style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '5px' }}>
                                {msg.attachments.map((file, i) => {
                                    const isImage = file.fileType === 'image' || /\.(jpg|jpeg|png|gif)$/i.test(file.url || "");
                                    const baseUrl = (process.env.REACT_APP_BASE_URL || "http://localhost:5026/api/v1").split('/api/v1')[0];
                                    const fileUrl = file.isLocal ? file.url : `${baseUrl}${file.url.startsWith('/public') ? file.url : `/public${file.url}`}`;

                                    if (isImage) {
                                        return <img key={i} src={fileUrl} alt="attachment" style={{ maxWidth: '200px', borderRadius: '8px', cursor: 'pointer' }} onClick={() => window.open(fileUrl, '_blank')} />;
                                    }
                                    const ext = (file.name?.split('.').pop() || '').toLowerCase();
                                    const iconBg = ext === 'pdf' ? '#ff4d4f' : ['doc','docx'].includes(ext) ? '#1a73e8' : ['xls','xlsx','csv'].includes(ext) ? '#0f9d58' : ['ppt','pptx'].includes(ext) ? '#ff6d00' : ['zip','rar','7z'].includes(ext) ? '#9c27b0' : '#5f6368';
                                    return (
                                        <a key={i} href={fileUrl} onClick={async (e) => { e.preventDefault(); try { const res = await fetch(fileUrl); const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = file.name || 'file'; a.click(); URL.revokeObjectURL(url); } catch { window.open(fileUrl, '_blank'); } }} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: 4, padding: '8px 12px', background: 'rgba(0,0,0,0.04)', borderRadius: '10px', textDecoration: 'none', border: '1px solid rgba(0,0,0,0.08)', maxWidth: '240px', cursor: 'pointer' }}>
                                            <div style={{ width: 36, height: 36, borderRadius: '8px', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                <FileText size={18} color="#fff" />
                                            </div>
                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                <div style={{ fontSize: '12px', fontWeight: '600', color: '#1d1d1d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name || "File"}</div>
                                                <div style={{ fontSize: '11px', color: '#5f6368', marginTop: '2px' }}>{ext ? ext.toUpperCase() : 'FILE'}</div>
                                            </div>
                                            <i className="uil-import" style={{ fontSize: '16px', color: '#5f6368', flexShrink: 0 }} />
                                        </a>
                                    );
                                })}
                            </div>
                        )}

                        {/* Reactions Display */}
                        {reactionsList.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '8px' }}>
                                {reactionsList.map((r, i) => (
                                    <div
                                        key={i}
                                        onClick={() => handleToggleReaction(msg._id, r.emoji)}
                                        style={{
                                            background: r.users?.includes(currentUserId) ? '#e8f0fe' : '#f1f3f4',
                                            border: r.users?.includes(currentUserId) ? '1px solid #1a73e8' : '1px solid #ddd',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '11px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <span>{r.emoji}</span>
                                        <span>{r.users?.length}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* URL Previews */}
                        {urls && urls.map((url, i) => {
                            const preview = urlPreviews[url];
                            if (!preview) return null;
                            return (
                                <a key={i} href={url} target="_blank" rel="noreferrer" className="url-preview-card">
                                    {preview.images?.[0] && <img src={preview.images[0]} alt="preview" />}
                                    <div className="url-info">
                                        <div className="url-title">{preview.title}</div>
                                        <div className="url-desc">{preview.description}</div>
                                        <div className="url-site">{preview.siteName || new URL(url).hostname}</div>
                                    </div>
                                </a>
                            );
                        })}
                        <div className="message-info-wrapper">
                            {showTime && (
                                <span className="message-time-inner">
                                    {moment(msg.createdAt).format("h:mm A")}
                                </span>
                            )}
                            {isOwn && (
                                <div className="message-status-ticks">
                                    {(() => {
                                        const r = msg.is_read;
                                        const sid = String(msg.sender_id);
                                        const isRead = Array.isArray(r)
                                            ? r.some(id => String(id) !== sid)
                                            : (r === true);
                                        return isRead
                                            ? <CheckCheck size={14} className="tick-read" />
                                            : <CheckCheck size={14} className="tick-delivered" />;
                                    })()}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderMessages = () => {
        let lastDate = null;

        const filteredMessages = chatSearchQuery.trim()
            ? messages.filter(m => m.text?.toLowerCase().includes(chatSearchQuery.toLowerCase()))
            : messages;

        if (filteredMessages.length === 0 && chatSearchQuery.trim()) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#70757a' }}>
                    <Search size={48} style={{ opacity: 0.2, marginBottom: '15px' }} />
                    <div>No results found for "{chatSearchQuery}"</div>
                </div>
            );
        }

        return filteredMessages.map((msg, idx) => {
            const msgDate = new Date(msg.createdAt).toDateString();
            let showDivider = false;

            if (msgDate !== lastDate) {
                lastDate = msgDate;
                showDivider = true;
            }

            return (
                <React.Fragment key={msg._id || idx}>
                    {showDivider && (
                        <div className="date-divider">
                            <span>{formatDateDivider(msg.createdAt)}</span>
                        </div>
                    )}
                    {(() => {
                        const prevMsg = filteredMessages[idx - 1];
                        const showTime = showDivider || !prevMsg || String(prevMsg.sender_id) !== String(msg.sender_id) || moment(prevMsg.createdAt).format("HH:mm") !== moment(msg.createdAt).format("HH:mm");
                        return renderMessage(msg, idx, showTime);
                    })()}
                </React.Fragment>
            );
        });
    };

    return (
        <>
            <Helmet><title>Chat - Planit</title></Helmet>
            <div className="main-content">
                <div className="page-content">
                    <div className="chat-container">
                        <RoomList
                            rooms={rooms}
                            activeRoom={activeRoom}
                            onSelectRoom={handleSelectRoom}
                            onNewChat={handleNewChat}
                            onSearchUsers={async (query) => {
                                try {
                                    const res = await ChatService.searchUsers(query);
                                    return res.Good ? res.users : [];
                                } catch { return []; }
                            }}
                            onStartDirectChat={handleStartDirectChat}
                            onCreateChannel={async (name, parentId) => {
                                if (!name.trim()) return;
                                try {
                                    const payload = { type: "channel", name };
                                    if (parentId) payload.parent_id = parentId;
                                    const res = await ChatService.getOrCreateRoom(payload);
                                    if (res.Good) {
                                        const newRoom = res.room;
                                        const newId = String(newRoom._id || newRoom.id || "");
                                        const existing = rooms.find(r => String(r._id || r.id || "") === newId);
                                        if (!existing) setRooms(prev => [newRoom, ...prev]);
                                        handleSelectRoom(existing || newRoom);
                                    }
                                } catch (err) {}
                            }}
                            currentUserId={currentUserId}
                        />

                        <div className="chat-main">
                            {activeRoom ? (
                                <>
                                    <div className="chat-header">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <Avatar size="large" src={getRoomDisplayAvatar(activeRoom)} style={{ color: "#fff", background: "#0DDFAB" }}>
                                                {getRoomDisplayName(activeRoom).charAt(0)}
                                            </Avatar>
                                            <div>
                                                <div style={{ fontWeight: '600', fontSize: '16px' }}>{getRoomDisplayName(activeRoom)}</div>
                                                <div style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {(() => {
                                                        if (activeRoom.type === 'direct') {
                                                            const otherMember = activeRoom.members.find(m => String(m.id || m._id) !== String(currentUserId));
                                                            return (otherMember?.isOnline || false) ? (
                                                                <span style={{ color: '#34a853', fontWeight: '500' }}>● Online</span>
                                                            ) : (
                                                                <span style={{ color: '#70757a' }}>Offline</span>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', gap: '15px' }}>
                                            <Tooltip title="Search in Chat">
                                                <Search
                                                    size={18}
                                                    color={isSearchVisible ? "#1a73e8" : "#5f6368"}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setIsSearchVisible(!isSearchVisible);
                                                        setChatSearchQuery("");
                                                    }}
                                                />
                                            </Tooltip>

                                            {activeRoom.type !== 'direct' && (
                                                <Tooltip title="Add People">
                                                    <UserPlus
                                                        size={18}
                                                        color="#5f6368"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setSearchQuery("");
                                                            setSearchResults([]);
                                                            setIsAddMemberModal(true);
                                                        }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Tooltip title="View Info">
                                                <Info size={18} color="#5f6368" style={{ cursor: 'pointer' }} onClick={() => setIsInfoModalVisible(true)} />
                                            </Tooltip>

                                            <Dropdown
                                                overlay={
                                                    <Menu>
                                                        <Menu.Item key="clear" icon={<Eraser size={14} />} onClick={handleClearChat}>
                                                            Clear Chat History
                                                        </Menu.Item>
                                                        {activeRoom.type !== 'direct' && (
                                                            <Menu.Item key="leave" icon={<LogOut size={14} />} onClick={handleLeaveRoom}>
                                                                Leave Channel
                                                            </Menu.Item>
                                                        )}
                                                    </Menu>
                                                }
                                                trigger={['click']}
                                            >
                                                <MoreVertical size={18} color="#5f6368" style={{ cursor: 'pointer' }} />
                                            </Dropdown>
                                        </div>
                                    </div>

                                    {isSearchVisible && (
                                        <div className="search-bar-container" style={{ padding: '10px 20px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            <Search size={16} color="#70757a" />
                                            <Input
                                                placeholder="Search messages..."
                                                variant="borderless"
                                                autoFocus
                                                value={chatSearchQuery}
                                                onChange={(e) => setChatSearchQuery(e.target.value)}
                                                style={{ flex: 1, padding: 0 }}
                                            />
                                            <X
                                                size={16}
                                                color="#70757a"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    setIsSearchVisible(false);
                                                    setChatSearchQuery("");
                                                }}
                                            />
                                        </div>
                                    )}

                                    <div className="chat-messages" style={{ flex: 1, padding: '15px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }} ref={scrollRef}>
                                        {/* Pinned Messages Bar */}
                                        {pinnedMessages.length > 0 && (
                                            <div style={{
                                                position: 'sticky',
                                                top: 0,
                                                zIndex: 10,
                                                background: '#e8f0fe',
                                                border: '1px solid #d2e3fc',
                                                borderRadius: '8px',
                                                padding: '8px 16px',
                                                marginBottom: '15px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden', height: '24px' }}>
                                                    <Avatar
                                                        size={18}
                                                        src={pinnedMessages[pinnedMessages.length - 1].sender_image}
                                                        style={{ minWidth: '18px', fontSize: '9px', background: '#1a73e8', color: '#fff' }}
                                                    >
                                                        {pinnedMessages[pinnedMessages.length - 1].sender_name?.charAt(0)}
                                                    </Avatar>
                                                    <div style={{
                                                        fontSize: '13px',
                                                        color: '#1967d2',
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        lineHeight: '24px',
                                                        fontWeight: '500'
                                                    }}>
                                                        {pinnedMessages[pinnedMessages.length - 1].text || "Attachment pinned"}
                                                    </div>
                                                </div>
                                                <Button size="small" type="link" style={{ fontSize: '12px' }} onClick={() => setShowPinnedDrawer(true)}>
                                                    View All ({pinnedMessages.length})
                                                </Button>
                                            </div>
                                        )}

                                        {msgLoading ? <ChatShimmer /> : renderMessages()}
                                    </div>

                                    <div className="typing-indicator">
                                        {Object.values(typingUsers[activeRoom._id] || {}).length > 0 && (
                                            <span>{Object.values(typingUsers[activeRoom._id]).join(", ")} is typing...</span>
                                        )}
                                    </div>

                                    <div className="message-input-area">
                                        <MessageInput
                                            onSend={handleSendMessage}
                                            users={mentionUsers}
                                            onTyping={handleTyping}
                                            replyTo={replyTo}
                                            onCancelReply={() => setReplyTo(null)}
                                        />
                                    </div>
                                </>
                            ) : (
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
                                    <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <circle cx="40" cy="40" r="40" fill="#F0FDF9" />
                                        <rect x="18" y="28" width="30" height="22" rx="4" fill="#0DDFAB" opacity="0.3" />
                                        <rect x="22" y="24" width="30" height="22" rx="4" fill="#0DDFAB" opacity="0.6" />
                                        <rect x="26" y="20" width="30" height="22" rx="4" fill="#0DDFAB" />
                                        <circle cx="56" cy="52" r="10" fill="#1D1D1D" />
                                        <path d="M52 52h8M56 48v8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <div style={{ textAlign: 'center' }}>
                                        <h4 style={{ fontWeight: 600, color: '#1D1D1D', marginBottom: '6px' }}>
                                            {isNewDmMode ? 'New Direct Message' : 'Private workspace'}
                                        </h4>
                                        <p style={{ fontSize: '14px', color: '#70757a', maxWidth: '320px', lineHeight: 1.6, marginBottom: 0 }}>
                                            {isNewDmMode
                                                ? 'Search for a person to start a private conversation.'
                                                : 'A place just for you to capture ideas, draft messages, and keep everything organized for later.'}
                                        </p>
                                    </div>
                                    {isNewDmMode && (
                                        <button
                                            onClick={() => {
                                                setModalType('dm');
                                                setSearchQuery('');
                                                setSearchResults([]);
                                                setIsNewChatModal(true);
                                            }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '8px',
                                                padding: '10px 24px', borderRadius: '8px',
                                                backgroundColor: '#0DDFAB', color: '#fff',
                                                border: 'none', cursor: 'pointer',
                                                fontSize: '14px', fontWeight: 600
                                            }}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3v10M3 8h10" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
                                            Add People
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal
                title={modalType === 'channel' ? "Create New Channel" : modalType === 'subchannel' ? "Create New Sub-channel" : "Add People"}
                visible={isNewChatModal}
                onCancel={() => {
                    setIsNewChatModal(false);
                }}
                footer={null}
                width={500}
            >
                {modalType === 'dm' ? (
                    <div style={{ padding: "10px 0" }}>
                        <Input
                            placeholder="Search users..."
                            style={{ borderRadius: '8px' }}
                            value={searchQuery}
                            onChange={(e) => handleSearchUsers(e.target.value)}
                            autoFocus
                        />
                        <div style={{ marginTop: '20px', maxHeight: '300px', overflowY: 'auto' }}>
                            {searchResults.map(u => (
                                <div key={u.id} className="chat-room-item" style={{ borderRadius: '8px', marginBottom: '5px' }} onClick={() => handleStartDirectChat(u.id)}>
                                    <Avatar src={u.image}>{u.first_name?.charAt(0)}</Avatar>
                                    <div style={{ marginLeft: '10px' }}>
                                        <div style={{ fontWeight: '500' }}>{u.first_name} {u.last_name}</div>
                                        <div style={{ fontSize: '11px', color: '#70757a' }}>{u.email}</div>
                                    </div>
                                </div>
                            ))}
                            {searchQuery.length > 1 && searchResults.length === 0 && (
                                <div style={{ textAlign: 'center', padding: '20px', color: '#70757a' }}>No users found</div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div style={{ padding: "10px 0" }}>
                        <div style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', color: '#5f6368', fontSize: '12px', fontWeight: '600' }}>CHANNEL NAME</label>
                            <Input
                                placeholder="e.g. general-discussions"
                                value={newChannelName}
                                onChange={(e) => setNewChannelName(e.target.value)}
                                maxLength={30}
                                autoFocus
                            />
                        </div>
                        <Button
                            type="primary"
                            block
                            style={{ height: '40px', borderRadius: '8px' }}
                            disabled={!newChannelName.trim()}
                            onClick={handleCreateChannel}
                        >
                            Create {modalType === 'subchannel' ? 'Sub-channel' : 'Channel'}
                        </Button>
                    </div>
                )}
            </Modal>

            <Modal
                title={`Add Member to ${getRoomDisplayName(activeRoom)}`}
                visible={isAddMemberModal}
                onCancel={() => {
                    setIsAddMemberModal(false);
                    setSearchQuery("");
                    setSearchResults([]);
                }}
                footer={null}
                width={500}
            >
                <div style={{ padding: "10px 0" }}>
                    <Input
                        placeholder="Search for a user to add..."
                        style={{ borderRadius: '8px' }}
                        value={searchQuery}
                        onChange={(e) => handleSearchUsers(e.target.value)}
                        autoFocus
                    />
                    <div style={{ marginTop: '20px', maxHeight: '300px', overflowY: 'auto' }}>
                        {searchResults.map(u => {
                            const isAlreadyMember = activeRoom?.members?.some(m => String(m.id || m._id) === String(u.id));
                            return (
                                <div
                                    key={u.id}
                                    className={`chat-room-item ${isAlreadyMember ? "disabled" : ""}`}
                                    style={{
                                        borderRadius: '8px',
                                        marginBottom: '5px',
                                        opacity: isAlreadyMember ? 0.6 : 1,
                                        cursor: isAlreadyMember ? 'default' : 'pointer',
                                        backgroundColor: isAlreadyMember ? '#f8f9fa' : 'transparent'
                                    }}
                                    onClick={() => !isAlreadyMember && handleConfirmAddMember(u.id)}
                                >
                                    <Avatar src={u.image}>{u.first_name?.charAt(0)}</Avatar>
                                    <div style={{ marginLeft: '10px' }}>
                                        <div style={{ fontWeight: '500' }}>{u.first_name} {u.last_name}</div>
                                        <div style={{ fontSize: '11px', color: '#70757a' }}>{u.email}</div>
                                    </div>
                                    <div style={{ marginLeft: 'auto', color: isAlreadyMember ? '#5f6368' : '#1a73e8', fontSize: '12px' }}>
                                        {isAlreadyMember ? "Already a member" : "Click to Add"}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Modal>

            {/* Pinned Messages Drawer */}
            <Drawer
                title="Pinned Messages"
                placement="right"
                onClose={() => setShowPinnedDrawer(false)}
                open={showPinnedDrawer}
                width={350}
            >
                {pinnedMessages.length === 0 ? (
                    <Empty description="No pinned messages yet" />
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {pinnedMessages.map((p, i) => (
                            <div
                                key={i}
                                onClick={() => scrollToMessage(p._id)}
                                style={{
                                    padding: '12px',
                                    border: '1px solid #e8eaed',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    transition: 'background: 0.2s'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.background = '#f8f9fa'}
                                onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                            >
                                <div style={{ fontWeight: '600', fontSize: '12px', marginBottom: '4px' }}>{p.sender_name}</div>
                                <div style={{ fontSize: '13px', color: '#3c4043', maxHeight: '60px', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                                    {p.text || "📷 Attachment"}
                                </div>
                                <div style={{ fontSize: '10px', color: '#70757a', marginTop: '8px' }}>
                                    {new Date(p.createdAt).toLocaleDateString()} at {new Date(p.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Drawer>

            {/* Chat Information Modal */}
            {activeRoom && (
                <Modal
                    title={`Chat Information`}
                    visible={isInfoModalVisible}
                    onCancel={() => setIsInfoModalVisible(false)}
                    footer={null}
                    width={400}
                >
                    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                        <Avatar size={64} src={getRoomDisplayAvatar(activeRoom)}>
                            {getRoomDisplayName(activeRoom).charAt(0)}
                        </Avatar>
                        <h3 style={{ marginTop: '10px', marginBottom: '4px' }}>{getRoomDisplayName(activeRoom)}</h3>
                        <div style={{ color: '#5f6368', fontSize: '12px', textTransform: 'capitalize' }}>{activeRoom.type} Chat</div>
                    </div>

                    <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                        <div style={{ fontWeight: '600', marginBottom: '10px', fontSize: '13px' }}>Members ({activeRoom.members?.length || 0})</div>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            {activeRoom.members?.map(m => (
                                <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0' }}>
                                    <Avatar src={m.image}>{m.first_name?.charAt(0)}</Avatar>
                                    <div>
                                        <div style={{ fontSize: '13px', fontWeight: '500' }}>{m.first_name} {m.last_name} {String(m.id) === String(currentUserId) && "(You)"}</div>
                                        <div style={{ fontSize: '11px', color: '#70757a' }}>{m.email}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default Chat;
